import React, { useContext, useState } from "react";
import { useCart } from "../context/CartContext"; // Import the custom hook
import axiosInstance from "../axiosInstance"; // Your axios instance for API requests
import { useNavigate } from "react-router-dom"; // Import useNavigate for navigation
import { FaArrowLeft, FaTrash, FaCheckCircle } from "react-icons/fa";
const { v4: uuidv4 } = require("uuid"); // Import the UUID function

const ViewCart = ({ closeViewCartModal }) => {
  const { state, dispatch } = useCart(); // Use the useCart hook to access cart state and dispatch
  const [userDetails, setUserDetails] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate(); // Create a navigate instance
  const [ orderCompleted, setOrderCompleted ] = useState(false); 
  const [orderedDetails, setOrderedDetails] = useState({});

  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setUserDetails({ ...userDetails, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    // Get restaurantId and tableNumber from cookies
    const restaurantId = getCookie("restaurantId");
    const tableNumber = getCookie("tableNumber");
    const orderDetails = {
      restaurantId: restaurantId,
      tableNumber: tableNumber,
      customerDetails: userDetails,
      foodDetails: state.items.map((item) => ({
        name: item.name,
        quantity: item.quantity,
        price: item.price,
        notes: item.notes,
        selectedExtras: item.selectedExtras.map((extra) => ({
          name: extra.name,
          price: extra.price,
        })),
      })),
      total: (state.items
        .reduce((total, item) => {
          // Calculate total for the main item price
          const itemTotal = item.price * item.quantity;

          // Calculate total for the selected extras
          const extrasTotal = item.selectedExtras.reduce((extrasSum, extra) => {
            return extrasSum + extra.price * item.quantity; // Multiply extras price by item quantity
          }, 0);

          // Add both item total and extras total
          return total + itemTotal + extrasTotal ; // Add service fee
        }, 0))
        .toFixed(2),
      status: "new",
    };

    try {
      const response = await axiosInstance.post("/api/orders", orderDetails);
      console.log("Order response:", response.data);
      setOrderCompleted(true);
      setOrderedDetails(response.data);
      setSuccess("Order placed successfully!");
      dispatch({ type: "CLEAR_CART" }); // Clear the cart
      setLoading(false);
    } catch (error) {
      console.error("Error placing order:", error);
      setError("Error placing order. Please try again.");
      setLoading(false);
    }
  };

  // Function to handle item removal from the cart
  const handleRemoveItem = (item) => {
    console.log("Removing item:", item);
    dispatch({ type: "REMOVE_ITEM", payload: item });
  };

  const closeThisModal = () => {
    closeViewCartModal();
  };

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
  };

  return (
    <div className="container mx-auto p-4 flex justify-center items-center ">
      {orderCompleted ? (
         <div className="w-full max-w-lg   inset-0 bg-gray-900 bg-opacity-50 flex justify-center  ">
         <div className="bg-white  shadow-lg w-100   relative">
           {/* Success Icon */}
           <div className="flex justify-center mb-4">
             <FaCheckCircle className="text-green-500 text-5xl" />
           </div>
   
           {/* Heading */}
           <h1 className="text-3xl font-bold text-center text-gray-800 mb-4">
             Order Successful!
           </h1>
   
           {/* Customer Name */}
           <h2 className="text-lg font-semibold text-center text-gray-700 mb-2">
             {orderedDetails.customerDetails.name}
           </h2>
   
           {/* Order Items List */}
           <div className="mt-6">
             <h3 className="text-lg font-bold text-gray-800">Order Items:</h3>
             <ul className="mt-2 space-y-2">
               {orderedDetails.foodDetails.map((food, index) => (
                 <li
                   key={index}
                   className="border-b py-2 flex justify-between items-center text-gray-700"
                 >
                   <span>{food.name}</span>
                   <span>Qty: {food.quantity}</span>
                 </li>
               ))}
             </ul>
           </div>
   
           {/* Total Price */}
           <div className="mt-6 text-right text-lg font-bold text-gray-800">
             Total: ${orderedDetails.total}
           </div>
   
           {/* Button to order more */}
           <button
             className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-orange-500 transition duration-200 w-full mt-6 flex items-center justify-center"
             onClick={closeThisModal}
           > 
             Order More Items
           </button>
           <button
             className="bg-orange-600 text-white py-2 px-4 rounded hover:bg-orange-500 transition duration-200 w-full mt-6 flex items-center justify-center"
             onClick={closeThisModal}
           > 
             Close
           </button>
         </div>
       </div>
      ) : (
        <div className="w-full max-w-lg">
          <h1 className="text-2xl font-bold mb-4 text-center">Your Cart</h1>

          {/* Show cart items */}
          {state.items.length === 0 ? (
            <p className="mt-4 text-gray-600 text-center">
              Your cart is empty.
            </p>
          ) : (
            <div>
              <ul className="mb-4">
                {state.items.map((item) => (
                  <li
                    key={item._id}
                    className="border-b py-2 flex items-center"
                  >
                    {/* Image on the left */}
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      className="w-16 h-16 object-cover rounded-md mr-2" // Adjust size for mobile
                    />
                    {/* Item details */}
                    <div className="flex-1 flex flex-col">
                      <h4 className="text-sm font-semibold">{item.name}</h4>
                      <p className="text-xs text-gray-600">
                        {item.extras && item.extras.length > 0 && (
                          <ul>
                            {item.extras.map((extra) => (
                              <li key={extra._id}>
                                {extra.name} (${extra.price})
                              </li>
                            ))}
                          </ul>
                        )}
                      </p>
                      <p className="text-xs text-gray-600">{item.notes}</p>
                      <p className="text-xs text-gray-600">
                        Quantity: {item.quantity}
                      </p>
                      <p className="text-xs font-bold">${item.total}</p>
                    </div>
                    {/* Remove button with X icon */}
                    <button
                      className="bg-red-500 text-white w-8 h-8 rounded-full flex items-center justify-center hover:bg-red-600 transition duration-200"
                      onClick={() => handleRemoveItem(item)} // Call remove function
                    >
                      <FaTrash className="text-sm" />
                    </button>
                  </li>
                ))}
              </ul>

              {/* Subtotal calculation */}
              {/* Subtotal calculation */}
              <div className="flex justify-between items-center  pt-2 mt-2">
               

              </div>
              <div className="flex justify-between items-center border-t pt-2 mt-2">
              

                <span className="font-bold">Subtotal:</span>
                <span className="font-bold">
                  $
                  {(state.items
                    .reduce((total, item) => {
                      // Calculate total for the main item price
                      const itemTotal = item.price * item.quantity;

                      // Calculate total for the selected extras
                      const extrasTotal = item.selectedExtras.reduce(
                        (extrasSum, extra) => {
                          return extrasSum + extra.price * item.quantity; // Multiply extras price by item quantity
                        },
                        0
                      );

                      // Add both item total and extras total
                      return total + itemTotal + extrasTotal ;
                    }, 0))
                    .toFixed(2)}
                </span>
              </div>

              {/* Order form */}
              <form id="order-form" className="mt-4" onSubmit={handleSubmit}>
                <h2 className="text-lg font-bold mb-2 text-center">
                  Your Details
                </h2>

                <div className="mb-2">
                  <label className="block mb-1 text-xs" htmlFor="name">
                    Name:
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="border p-1 rounded w-full text-sm"
                    onChange={handleChange} // Add your state management
                  />
                </div>

                <div className="mb-2">
                  <label className="block mb-1 text-xs" htmlFor="email">
                    Email:
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="border p-1 rounded w-full text-sm"
                    onChange={handleChange} // Add your state management
                  />
                </div>

                <div className="mb-2">
                  <label className="block mb-1 text-xs" htmlFor="phone">
                    Phone:
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    className="border p-1 rounded w-full text-sm"
                    onChange={handleChange} // Add your state management
                  />
                </div>

                <button
                  type="submit"
                  className="bg-green-500 text-white py-1 px-2 rounded hover:bg-green-600 transition duration-200 w-full text-sm"
                >
                  Place Order {loading && "(Loading...)"}{" "}
                  {error && `(${error})`}
                </button>
                {/* Back to ordering page button */}
                <button
                  className="bg-orange-600  text-white py-1 px-2 rounded hover:bg-orange-600 transition duration-200 w-full text-sm mt-2"
                  onClick={closeThisModal}
                >
                  <FaArrowLeft className="inline-block mr-1 mb-1" />
                  Back
                </button>
              </form>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ViewCart;
